import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useMemo } from 'react'

import { selectHistoricalRunsheetsWithShifts } from '@/entities/schedules'
import { clearHistoricalRunsheets } from '@/entities/schedules/thunks/clearHistoricalRunsheets'
import { fetchHistoricalRunsheets } from '@/entities/schedules/thunks/fetchHistoricalRunsheets'
import { widgetWindowsSelector } from '@/entities/widgetWindows'
import { showShiftOnMap } from '@/entities/widgetWindows/thunks/showShiftOnMap'
import { OLMSApiService } from '@/shared/apis'
import { Button, Icon } from '@/shared/components'
import { DatePicker } from '@/shared/components/DatePicker/DatePicker'
import { Dropdown } from '@/shared/components/Dropdown/Dropdown'
import { DropdownItem } from '@/shared/components/Dropdown/DropdownItem'
import { useDialogContext } from '@/shared/components/Modal/DialogContext'
import { TREE_WIDGETS } from '@/shared/components/Mosaic/models/widgetType'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faArrowUpRightFromSquare, faCircle, faCircleDot, faTruck } from '@fortawesome/pro-regular-svg-icons'
import { faFilter } from '@fortawesome/pro-solid-svg-icons'
import { Box, CircularProgress, Divider, styled, Typography } from '@mui/material'
import { TreeViewBaseItem } from '@mui/x-tree-view/models'

import { useMosaicLayoutContext } from '../MosaicLayout/MosaicLayoutContext'
import { EditShiftDialog } from '../OperationsDialogs/EditShiftDialog/EditShiftDialog'
import { CustomTree } from './CustomTree'
import { CustomTreeItemType } from './model/CustomTreeItemType'
import { TreeItemAction } from './model/TreeItemAction'

export type HistoricalRunsheetTreeProps = {
  //
}

export const HistoricalRunsheetTree: React.FC<HistoricalRunsheetTreeProps> = () => {
  const [scheduledDates, setScheduledDates] = React.useState<Dayjs[]>([])
  const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(null)
  const [searchFutureSevenDays, setSearchFutureSevenDays] = React.useState<boolean>(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const { closeWidgetWindows } = useMosaicLayoutContext()
  const { openDialog } = useDialogContext()
  const dispatch = useAppDispatch()

  const historicalRunsheets = useAppSelector(state => selectHistoricalRunsheetsWithShifts(state))
  const runsheetIds = historicalRunsheets.map(runsheet => runsheet.id)
  const historicalWidgetWindows = useAppSelector(state => widgetWindowsSelector.selectAll(state))
    .filter(window => (window.widgetType === TREE_WIDGETS.RunsheetShiftTable ||
      window.widgetType === TREE_WIDGETS.RunsheetUnassignedOrders) &&
      runsheetIds.includes(window.runsheetId))

  // Pull the historical scheduled dates
  useEffect(() => {
    OLMSApiService.get<string[]>('/runsheet/scheduledDates').then((results) => {
      if (!results) return

      const dates = results.map(date => dayjs(date, 'DD/MM/YYYY'))

      setScheduledDates(dates)
    })
  }, [])

  // Reset the filter
  const onClear = async () => {
    setSelectedDate(null)
    setSearchFutureSevenDays(false)

    // Close the widget windows
    closeWidgetWindows(historicalWidgetWindows.map(window => window.id))

    // Clear the runsheets from the redux store.
    await dispatch(clearHistoricalRunsheets())
  }

  const onSearch = async () => {
    if (!selectedDate) return
    setIsLoading(true)

    closeWidgetWindows(historicalWidgetWindows.map(window => window.id))
    await dispatch(clearHistoricalRunsheets())
    await dispatch(fetchHistoricalRunsheets({
      date: selectedDate.format('YYYY-MM-DD'),
      dateRangeOption: searchFutureSevenDays ? 2 : 1,
    }))

    setIsLoading(false)
  }

  // Runsheet Tree
  const historicalRunsheetTree: TreeViewBaseItem<CustomTreeItemType>[] = useMemo(() => {
    const tree = historicalRunsheets?.map(runsheet => {
      let children: TreeViewBaseItem<CustomTreeItemType>[] = []

      // Add Unassigned Orders
      const unassignedOrders = {
        id: `${runsheet.id}-unassigned`,
        level: 1,
        runsheetId: runsheet.id,
        label: `Unassigned (${runsheet.unassignedOrderCount})`,
        widgetType: TREE_WIDGETS.RunsheetUnassignedOrders,
        isDraggable: true,
        isDroppable: false,
        acceptDropTypes: [],
        actions: [],
      }

      children.push(unassignedOrders)

      // Add Shifts
      const shifts = runsheet.shifts.map(shift => {
        const shActions: TreeItemAction[] = [
          {
            type: 'actionItem',
            label: 'View Run',
            icon: faTruck,
            action: () => {
              openDialog(EditShiftDialog, {
                shiftId: shift.id,
                formType: 'view',
                onClose: () => {
                  // Any callback after the dialog is closed
                },
              })
            },
          },
          {
            type: 'divider',
          },
          {
            type: 'actionItem',
            label: 'Show On Map',
            icon: faArrowUpRightFromSquare,
            action: () => {
              dispatch(showShiftOnMap({ shiftId: shift.id }))
            },
          },
        ]

        return {
          id: shift.id,
          level: 1,
          shiftId: shift.id,
          runsheetId: runsheet.id,
          label: `${shift.name} (${shift.orderCount})`,
          uiColor: shift.uiColor,
          widgetType: TREE_WIDGETS.RunsheetShiftTable,
          isDraggable: true,
          isDroppable: false,
          acceptDropTypes: [],
          actions: shActions,

          // For Utilisation
          showUtilisationPie: false,
          maxVolume: shift.maxVolume,
          maxWeight: shift.maxWeight,
          maxWorkingTimeInMins: shift.maxWorkingTimeInMins,
          totalWorkingTimeInMins: shift.totalWorkingTimeInMins,
          volumeCapacity: shift.volumeCapacity,
          weightCapacity: shift.weightCapacity,
          loads: shift.loads,
        }
      })

      children = children.concat(shifts)

      return {
        id: runsheet.id,
        level: 0,
        runsheetId: runsheet.id,
        label: runsheet.name,
        widgetType: TREE_WIDGETS.RunsheetSummaryTable,
        children: children,
        isDraggable: true,
        isDroppable: false,
        acceptDropTypes: [],
        actions: [],
      }
    }) || []

    return tree
  }, [dispatch, historicalRunsheets, openDialog])


  return (
    <>
      <StyledFilter>
        <DatePicker
          label="Date of Operation"
          format="DD/MM/YYYY"
          disableFuture
          value={selectedDate}
          onChange={(newValue) => setSelectedDate(newValue)}
          highlightedDates={scheduledDates}
          slotProps={{
            textField: {
              fullWidth: true,
            },
          }}
        />
        <Dropdown
          icon={<StyledTreeItemIcon icon={faFilter} />}
          value="Filter"
          isIconButton
          slotProps={{
            iconButton: {
              round: true,
              sx: { marginLeft: 0, marginRight: 0, marginBottom: pxToRem(8) },
            },
          }}
        >
          <DropdownItem onSelect={() => { setSearchFutureSevenDays(false) }}>
            <Icon icon={searchFutureSevenDays ? faCircle : faCircleDot} />
            Search on the date
          </DropdownItem>
          <DropdownItem onSelect={() => { setSearchFutureSevenDays(true) }}>
            <Icon icon={searchFutureSevenDays ? faCircleDot : faCircle} />
            Search 7 days from the date
          </DropdownItem>
        </Dropdown>
      </StyledFilter>
      <StyledActionsContainer>
        <StyledButton
          text
          onClick={onClear}
          tooltipTitle="Clear filters and results"
          sx={{ marginRight: pxToRem(8) }}
        >
          Clear
        </StyledButton>
        <StyledButton
          onClick={onSearch}
          tooltipTitle="Search"
          disabled={!selectedDate}
        >
          Search
        </StyledButton>
      </StyledActionsContainer>
      <Box
        sx={{
          width: '100%',
          marginBottom: pxToRem(8),
          display: 'flex',
          justifyContent: 'center',
        }}>
        <Divider />
        {!isLoading && historicalRunsheets.length === 0 && (
          <StyledSecondaryText
            variant="body1"
          >
            Select a date and click Search to view historical runsheets.
          </StyledSecondaryText>
        )}
        {isLoading && (
          <CircularProgress size={24} sx={{ marginTop: pxToRem(24) }} />
        )}
      </Box>

      {!isLoading && <CustomTree
        items={historicalRunsheetTree}
      />}
    </>
  )
}

const StyledFilter = styled('div')`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding-left: ${props => props.theme.typography.pxToRem(8)};
`

const StyledTreeItemIcon = styled(Icon)`
  font-size: ${props => props.theme.typography.pxToRem(14)};
  width: ${props => props.theme.typography.pxToRem(14)};
  height: ${props => props.theme.typography.pxToRem(14)};
`

const StyledButton = styled(Button)`
  padding-left: ${props => props.theme.typography.pxToRem(8)};
  float: right;
`

const StyledActionsContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`

const StyledSecondaryText = styled(Typography)`
  margin-top: ${props => props.theme.typography.pxToRem(24)};
  color: ${props => props.theme.palette.text.secondary};
  text-align: center;
`